/**
 * LoginScreen (public) — split-screen "Acceso" per the Vera design system:
 * a dark institutional Tinta panel on the left (mark + tagline + seal
 * watermark) and the sign-in panel on the right. On narrow viewports the left
 * panel collapses and only the sign-in panel shows.
 *
 * Auth states (right panel):
 *  - LOADING    : while the initial getMe identity check runs.
 *  - SIGNED IN  : redirect to the intended destination or /rounds.
 *  - FORBIDDEN  : getMe 403 (AUTH_FORBIDDEN_ROLE) — sign-in succeeded but the
 *    account's role/domain is not allowlisted. Dedicated state + sign-out escape
 *    hatch (otherwise the same account re-authenticates and 403s forever).
 *  - OTHER ERROR: network / 5xx from getMe — surfaced via ProblemAlert.
 *  - SIGN IN    : cloud → Google button OR email/contraseña form (build-time
 *    VITE_AUTH_METHOD; password accounts are provisioned manually — no signup);
 *    local/dev → automatic pass-through note.
 *
 * Identity/role are SERVER-VERIFIED values from GET /v1/me — never client claims.
 * There is no backend /login; cloud sign-in is Firebase (popup or password).
 */

import { useState } from "react";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { isApiError } from "../api/client";
import { DemoBadge } from "../components/DemoBadge";
import { EmptyState } from "../components/EmptyState";
import { Loading } from "../components/Loading";
import { ProblemAlert } from "../components/ProblemAlert";
import { VeraMarkLine, VeraSeal } from "../components/Logo";
import { TENANT_BRANDED, TENANT_LOGO, TENANT_NAME } from "../branding";

type FromState = { from?: { pathname?: string } };

/** Two-column shell: dark Tinta brand panel + the sign-in panel (children). */
function LoginFrame({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1.1fr 1fr" },
        bgcolor: "background.default",
      }}
    >
      {/* Left — institutional panel (hidden on narrow screens) */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "space-between",
          position: "relative",
          overflow: "hidden",
          bgcolor: "primary.dark",
          color: "var(--paper)",
          px: 6.5,
          py: 6,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {TENANT_BRANDED ? (
            // The tenant lockup replaces the Vera mark; the flat PNG is tinted
            // to paper via filter so it reads on the dark institutional panel.
            <Box
              component="img"
              src={TENANT_LOGO}
              alt={TENANT_NAME || "Logotipo institucional"}
              sx={{
                height: 40,
                maxWidth: "100%",
                objectFit: "contain",
                display: "block",
                filter: "brightness(0) invert(1)",
                opacity: 0.95,
              }}
            />
          ) : (
            <>
              <VeraMarkLine size={34} style={{ color: "var(--paper)" }} />
              <Typography sx={{ fontWeight: 700, fontSize: 24, letterSpacing: "-0.02em" }}>
                Vera
              </Typography>
            </>
          )}
        </Box>

        <Box sx={{ maxWidth: 400 }}>
          {!TENANT_BRANDED && (
            <Box
              className="vera-overline"
              sx={{ color: "rgba(250,250,247,.6)", mb: 1.75 }}
            >
              Verificación de identidad
            </Box>
          )}
          <Typography
            component="h1"
            sx={{ fontSize: 30, fontWeight: 600, lineHeight: 1.2, letterSpacing: "-0.01em", mb: 1.75 }}
          >
            {TENANT_BRANDED
              ? "Consola de validación de documentos"
              : "Establecer lo que es verdadero sobre un documento, una persona, un momento."}
          </Typography>
          <Typography sx={{ fontSize: 14, lineHeight: 1.55, color: "rgba(250,250,247,.72)" }}>
            {TENANT_BRANDED
              ? "El acceso queda registrado y es auditable."
              : "Plataforma interna de validación documental. El acceso queda registrado y es auditable."}
          </Typography>
        </Box>

        {/* Vera brand texture — kept as a subtle watermark on branded consoles too. */}
        <VeraSeal
          size={240}
          style={{
            position: "absolute",
            right: -48,
            bottom: -48,
            color: "rgba(250,250,247,.10)",
          }}
        />
      </Box>

      {/* Right — sign-in panel */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 3, md: 5 },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 340 }}>
          {/* On narrow screens the brand mark moves into the sign-in panel.
              Branded builds skip it — the tenant logo already heads the panel. */}
          {!TENANT_BRANDED && (
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                alignItems: "center",
                gap: 1.25,
                mb: 3,
                color: "primary.main",
              }}
            >
              <VeraMarkLine size={30} />
              <Typography sx={{ fontWeight: 700, fontSize: 22, letterSpacing: "-0.02em" }}>
                Vera
              </Typography>
            </Box>
          )}
          {children}
        </Box>
      </Box>
    </Box>
  );
}

export default function LoginScreen() {
  const { user, role, loading, error, canSignIn, signIn, signOut, authMethod } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Local channel: the Firebase popup itself threw (cancelled / closed / popup
  // blocked). Distinct from `error`, which is a getMe rejection after a
  // successful popup (e.g. the 403 forbidden case).
  const [signInError, setSignInError] = useState<Error | null>(null);
  const [busy, setBusy] = useState(false);

  if (loading) {
    return <Loading label="Verificando sesión…" />;
  }

  // Signed in (and authorized): go to the intended destination or /rounds.
  if (user) {
    void role; // role is server-verified; route guards enforce per-screen access.
    const dest = (location.state as FromState | null)?.from?.pathname ?? "/rounds";
    return <Navigate to={dest} replace />;
  }

  const handleSignIn = async (credentials?: { email: string; password: string }) => {
    setBusy(true);
    setSignInError(null);
    try {
      await signIn(credentials);
      // Success: Firebase's onAuthStateChanged re-runs getMe; this screen then
      // either redirects (authorized) or shows the forbidden state (403).
    } catch (err: unknown) {
      setSignInError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(false);
    }
  };

  const handleSignOut = async () => {
    setBusy(true);
    try {
      await signOut();
    } finally {
      setBusy(false);
    }
  };

  // FORBIDDEN: getMe returned 403 (valid token, role/domain not allowlisted).
  if (isApiError(error) && error.isForbidden) {
    return (
      <LoginFrame>
        <Stack spacing={2}>
          <EmptyState
            icon={<LockOutlinedIcon fontSize="inherit" />}
            title="Acceso denegado"
            description="Tu cuenta no tiene permiso para acceder a esta consola. Verifica que uses la cuenta institucional autorizada o solicita acceso a un administrador."
            action={
              canSignIn ? (
                <Button
                  variant="outlined"
                  onClick={() => void handleSignOut()}
                  disabled={busy}
                >
                  Cerrar sesión e intentar con otra cuenta
                </Button>
              ) : undefined
            }
          />
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
            AUTH_FORBIDDEN_ROLE
          </Typography>
        </Stack>
      </LoginFrame>
    );
  }

  return (
    <LoginFrame>
      {TENANT_LOGO && (
        <Box
          component="img"
          src={TENANT_LOGO}
          alt={TENANT_NAME || "Logotipo institucional"}
          sx={{ height: 44, maxWidth: "100%", objectFit: "contain", display: "block", mb: 3 }}
        />
      )}
      <Typography component="h2" sx={{ fontSize: 21, fontWeight: 600, mb: 0.5 }}>
        Acceso de operador
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Use sus credenciales institucionales.
      </Typography>

      <DemoBadge />

      {/* Non-auth getMe failure (network / 5xx). 401 is normal pre-sign-in and
          is NOT surfaced as an error by AuthProvider. */}
      {error && <ProblemAlert error={error} />}
      {/* Popup-level failure (cancelled / blocked). */}
      {signInError && <ProblemAlert error={signInError} />}

      {canSignIn && authMethod === "password" ? (
        // Email/contraseña — accounts are provisioned manually by an admin,
        // so there is deliberately NO signup or self-service reset link.
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSignIn({ email: email.trim(), password });
          }}
        >
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Correo institucional"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
              size="small"
            />
            <TextField
              label="Contraseña"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              fullWidth
              size="small"
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              startIcon={<LockOutlinedIcon />}
              disabled={busy || !email.trim() || !password}
              fullWidth
            >
              Iniciar sesión
            </Button>
            <Typography variant="caption" color="text.secondary">
              Las cuentas las crea el administrador del sistema; si no tiene una
              o la olvidó, contacte a su administrador.
            </Typography>
          </Stack>
        </Box>
      ) : canSignIn ? (
        <Button
          variant="contained"
          size="large"
          startIcon={<GoogleIcon />}
          onClick={() => void handleSignIn()}
          disabled={busy}
          fullWidth
          sx={{ mt: 1 }}
        >
          Iniciar sesión con Google
        </Button>
      ) : (
        <Box
          sx={{
            mt: 1,
            p: 2,
            borderRadius: 1,
            bgcolor: "var(--surface-sunken)",
            border: "1px solid var(--border-hairline)",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Modo local/desarrollo: no se requiere inicio de sesión.
          </Typography>
        </Box>
      )}

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 2.5, lineHeight: 1.5 }}
      >
        El sistema es de uso exclusivo del personal autorizado. Toda actividad se
        registra conforme al protocolo de auditoría.
      </Typography>
    </LoginFrame>
  );
}
